import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {MatButton} from "@angular/material/button";
import {MatCheckbox} from "@angular/material/checkbox";
import {MAT_DIALOG_DATA, MatDialogClose, MatDialogRef} from "@angular/material/dialog";
import {MatError, MatFormField} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatTooltip} from "@angular/material/tooltip";
import {ReactiveFormsModule} from "@angular/forms";
import {BusyService} from "../../services/busy.service";
import {DomainService} from "../../services/domain.service";
import {UtilService} from "../../services/util.service";
import {Constants} from "../../util/constants";
import {finalize} from "rxjs/operators";
import {form, FormField, required, validate} from "@angular/forms/signals";
import {FieldErrorsComponent} from "../../controls/field-errors/field-errors.component";

export interface EditFlagDialogData {
    flagName: string;
    preventsDelete: boolean;
    allFlags: string[];
}

interface FlagModel {
    name: string;
    preventsDelete: boolean;
}

@Component({
    imports: [
        MatButton,
        MatCheckbox,
        MatDialogClose,
        MatFormField,
        MatInput,
        MatTooltip,
        ReactiveFormsModule,
        FieldErrorsComponent,
        MatError,
        FormField
    ],
    templateUrl: './edit-flag-dialog.component.html',
    styleUrl: './edit-flag-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditFlagDialogComponent {
    protected dialogData: EditFlagDialogData = inject(MAT_DIALOG_DATA);

    flagModel = signal<FlagModel>({
        name: this.dialogData.flagName,
        preventsDelete: this.dialogData.preventsDelete,
    });
    flagForm = form(this.flagModel, (schemaPath) => {
        required(schemaPath.name, {message: Constants.ERR_REQUIRED});
        validate(schemaPath.name, ({value}) => {
            const nameHasChanged = value() != this.dialogData.flagName;
            if (nameHasChanged && this.dialogData.allFlags.includes(value().toLowerCase())) {
                return {kind: 'taken', message: "Flag name already taken"}
            }
            return null;
        })
    });

    constructor(private busyService: BusyService,
                private domainService: DomainService,
                private utilService: UtilService,
                private dialogRef: MatDialogRef<EditFlagDialogComponent>) {
    }

    canSubmit = computed(() => {
        // We can submit if the form is valid and the name or boolean has changed
        // and new name doesn't collide with any existing names
        const newName: string = this.flagModel().name.toLowerCase();
        const newPreventsDelete: boolean = this.flagModel().preventsDelete;
        const nameHasChanged = newName != this.dialogData.flagName;
        const booleanHasChanged = newPreventsDelete != this.dialogData.preventsDelete;
        const nameExists = this.dialogData.allFlags.includes(newName);
        return this.flagForm().valid() &&
            (nameHasChanged || booleanHasChanged) &&
            (!nameHasChanged || !nameExists)  ; // nameHasChanged => !nameExists
    });

    submit(event: SubmitEvent) {
        event.preventDefault();
        this.busyService.showBusy();
        const newName: string = this.flagModel().name.toLowerCase();
        const preventsDelete: boolean = this.flagModel().preventsDelete;
        this.domainService.updateFlag(this.dialogData.flagName, newName, preventsDelete).pipe(
            finalize(() => {this.busyService.showNotBusy()})
        ).subscribe({
            next: () => {
                this.dialogRef.close(true);
            },
            error: this.utilService.getErrorHandler("Error updating flag")
        });
    }
}
